/**
 * Subdomain utilities for Pro+ users
 * Handles username.edumi.co routing
 */

export interface SubdomainContext {
  isSubdomain: boolean;
  username: string | null;
  uid: string | null;
  plan: string | null;
  isSalePageSubdomain?: boolean;
}

export function getSubdomainContext(): SubdomainContext {
  if (typeof window === 'undefined') {
    return {
      isSubdomain: false,
      username: null,
      uid: null,
      plan: null,
      isSalePageSubdomain: false
    };
  }

  const injected = (window as any).__EDUMI_SUBDOMAIN__;
  console.log('[Subdomain] Injected data:', injected);
  console.log('[Subdomain] Hostname:', window.location.hostname);
  
  if (injected) {
    console.log('[Subdomain] Using injected data from Cloudflare Worker');
    return {
      isSubdomain: true,
      username: injected.username || null,
      uid: injected.uid || null,
      plan: injected.plan || null,
      isSalePageSubdomain: injected.isSalePageSubdomain || false
    };
  }

  const hostname = window.location.hostname;
  const mainDomain = 'edumi.co';

  // Dedicated Sale Page host (Firebase Hosting site — served directly, no Cloudflare Worker)
  if (hostname === 'saledi.web.app' || hostname === 'saledi.firebaseapp.com') {
    console.log('[Subdomain] Saledi Sale Page host detected');
    return {
      isSubdomain: true,
      username: null,
      uid: null,
      plan: null,
      isSalePageSubdomain: true
    };
  }

  // Check for Coursedee custom domain (คอร์สดี.com)
  const isCoursedeeDomain = hostname === 'aiforyou.web.app' ||
                           hostname === 'www.aiforyou.web.app';
  
  if (isCoursedeeDomain) {
    console.log('[Subdomain] Coursedee custom domain detected');
    return {
      isSubdomain: false,
      username: null,
      uid: null,
      plan: null,
      isSalePageSubdomain: false
    };
  }
  
  if (hostname === mainDomain || hostname === `www.${mainDomain}`) {
    console.log('[Subdomain] Main domain detected');
    return {
      isSubdomain: false,
      username: null,
      uid: null,
      plan: null,
      isSalePageSubdomain: false
    };
  }

  if (hostname.endsWith(`.${mainDomain}`)) {
    const subdomain = hostname.slice(0, -(mainDomain.length + 1));
    console.log('[Subdomain] Subdomain detected from hostname:', subdomain);
    
    if (subdomain === 'salepage') {
      console.log('[Subdomain] Sale Page subdomain detected');
      return {
        isSubdomain: true,
        username: null,
        uid: null,
        plan: null,
        isSalePageSubdomain: true
      };
    }
    
    if (subdomain && !subdomain.includes('.')) {
      return {
        isSubdomain: true,
        username: subdomain,
        uid: null,
        plan: null,
        isSalePageSubdomain: false
      };
    }
  }

  console.log('[Subdomain] No subdomain detected');
  return {
    isSubdomain: false,
    username: null,
    uid: null,
    plan: null,
    isSalePageSubdomain: false
  };
}

export function isProPlusSubdomain(): boolean {
  const context = getSubdomainContext();
  return context.isSubdomain && (context.plan === 'pro_plus_monthly' || context.plan === 'pro_plus_yearly' || context.plan === 'yearly');
}

export function getSubdomainUsername(): string | null {
  const context = getSubdomainContext();
  return context.username;
}

export function buildSubdomainUrl(username: string, path: string = '/'): string {
  const protocol = window.location.protocol;
  return `${protocol}//${username}.edumi.co${path}`;
}

export function buildMainDomainUrl(path: string = '/'): string {
  const protocol = window.location.protocol;
  return `${protocol}//coursedee.web.app${path}`;
}

export function isSalePageSubdomain(): boolean {
  const context = getSubdomainContext();
  return context.isSalePageSubdomain === true;
}

export function isLocalDevelopment(): boolean {
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname.includes('localhost');
}
